import React, { useEffect, useRef } from 'react';

// Import React Google Maps
// https://www.npmjs.com/package/react-google-maps
import { withScriptjs, withGoogleMap, GoogleMap } from "react-google-maps";

/*
  This component renders an interactive Google map using React Google Maps.

  This component takes the following props:

  * mapRef ... the ref of the Google Map; exposes API methods
  * setMapRef ... sets the Google Map ref
  * googleMapURL ... a valid Google Maps URL (which includes an API key)
  * loadingElement, containerElement, mapElement ... options for React Canvas Draw
    https://www.npmjs.com/package/react-canvas-draw
  * mapCenter ... a lat/lng object to use as the center of the map
  * getCenterData ... a helper function which determines the center of the map
  * zoomLevel ... a numeric zoom level to use for the map
  * onZoomChanged ... a callback function used when zoom level changes on the map
*/

const MapComponent = withScriptjs(withGoogleMap(
  (props) => {
    const mapInstanceRef = useRef(null);
    const lastProgrammaticCenterRef = useRef(null);

    // Pan map to new center only for significant changes (address search)
    // Small changes from user drag are ignored to preserve smooth drag behavior
    useEffect(() => {
      if (!mapInstanceRef.current || !props.mapCenter) return;

      const { lat, lng } = props.mapCenter;
      const lastCenter = lastProgrammaticCenterRef.current;

      // Skip if this is the same as our last programmatic pan target
      if (lastCenter && lastCenter.lat === lat && lastCenter.lng === lng) {
        return;
      }

      // Check if this is a significant move (likely from address search)
      // vs a small move (likely from user drag being reported back)
      if (lastCenter) {
        const latDiff = Math.abs(lat - lastCenter.lat);
        const lngDiff = Math.abs(lng - lastCenter.lng);
        // ~0.001 degrees ≈ 111 meters - if less than this, it's likely user drag feedback
        if (latDiff < 0.001 && lngDiff < 0.001) {
          return;
        }
      }

      // This is a significant move - execute programmatic pan
      lastProgrammaticCenterRef.current = { lat, lng };
      mapInstanceRef.current.panTo({ lat, lng });
    }, [props.mapCenter]);

    const handleMapRef = (ref) => {
      mapInstanceRef.current = ref;
      props.setMapRef(ref);
    };

    return (
      <GoogleMap
        ref={handleMapRef}
        defaultZoom={props.zoomLevel}
        defaultCenter={props.mapCenter}
        mapTypeId="hybrid"
        onCenterChanged={()=>{ 
          props.getCenterData();
        }}
        tilt={0}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: "greedy",
          disableDefaultUI: true
        }}
        zoom={props.zoomLevel}
        onZoomChanged={props.onZoomChanged}
        className="map-view"
      >
      </GoogleMap>
    );
  }
))

export default MapComponent;
